import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Mail,
  Phone,
  Shield,
  Zap,
  Users,
  Crown,
  Gamepad2,
  Download,
  Server,
  Home,
  MessageCircle,
  Video
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { pathname } = useLocation();

  const quickLinks = [
    { name: 'خانه', path: '/', icon: Home },
    { name: 'خرید رنک', path: '/ranks', icon: Crown },
    { name: 'پنل کاربری', path: '/user-panel', icon: Users },
    { name: 'تیکت پشتیبانی', path: '/tickets', icon: MessageCircle },
  ];

  const features = [
    { name: 'امنیت بالا', icon: Shield },
    { name: 'عملکرد عالی', icon: Zap },
    { name: 'جامعه فعال', icon: Users },
    { name: 'پشتیبانی آنلاین', icon: Phone },
  ];

  const socialLinks = [
    { name: 'روبیکا', url: 'https://rubika.ir/AvalonMc', icon: MessageCircle },
    { name: 'آپارات', url: 'https://www.aparat.com/AvalonMC', icon: Video },
  ];

  return (
    <footer className="bg-[#03050a] text-white border-t border-white/10" dir="rtl">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg border border-white/10 shrink-0 bg-white/5 p-1 flex items-center justify-center">
                <img src="/favicon.ico" alt="AvalonMC Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  آوالون ام‌سی
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">بزرگترین سرور ماینکرفت بدراک ایران</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              نقطه تلاقی قدرت، نوآوری و تجربه بی‌نظیر بازی. جایی که رویاهای شما به واقعیت تبدیل می‌شود.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all text-gray-300"
                  title={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base font-bold mb-6 text-white border-r-2 border-emerald-500 pr-3">دسترسی سریع</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2.5 text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    <div className="w-6 h-6 bg-white/5 rounded-lg flex items-center justify-center border border-white/10">
                      <link.icon className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Features */}
          <div>
            <h4 className="text-base font-bold mb-6 text-white border-r-2 border-cyan-500 pr-3">ویژگی‌های ما</h4>
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2.5 text-gray-400">
                  <div className="w-6 h-6 bg-white/5 rounded-lg flex items-center justify-center border border-white/10">
                    <feature.icon className="w-3.5 h-3.5 text-cyan-400" />
                  </div>
                  <span className="text-sm">{feature.name}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-base font-bold mb-6 text-white border-r-2 border-emerald-500 pr-3">اطلاعات تماس</h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-gray-400">
                <div className="w-8 h-8 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center shrink-0">
                  <Server className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">آدرس سرور ماینکرفت</p>
                  <p className="text-sm font-mono text-white">play.avbe.ir:19132</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-gray-400">
                <div className="w-8 h-8 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">ایمیل پشتیبانی</p>
                  <p className="text-sm text-white">AvalonMcPe@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-gray-400">
                <div className="w-8 h-8 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">شماره پشتیبانی</p>
                  <p className="text-sm text-white">پاسخگویی از طریق تیکت</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6 text-center text-xs text-gray-500">
        © {currentYear} آوالون ام‌سی - تمامی حقوق برای سرور اوالون ام‌سی محفوظ است.
      </div>

      {/* Back to Top Button */}
      {!pathname.startsWith('/admin-panel') && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 left-6 w-11 h-11 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-2xl shadow-xl hover:opacity-90 transition-all z-50 flex items-center justify-center"
        >
          ↑
        </button>
      )}
    </footer>
  );
};

export default Footer;
